import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { useEffect } from "react";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { ConsultantDashboard } from "./pages/ConsultantDashboard";
import { ConsultantInterventions } from "./pages/ConsultantInterventions";
import { InterventionForm } from "./pages/InterventionForm";
import { InterventionDetail } from "./pages/InterventionDetail";
import { SMEDashboard } from "./pages/SMEDashboard";
import { SMEPendingValidations } from "./pages/SMEPendingValidations";
import { SMEHistory } from "./pages/SMEHistory";
import { FeedbackForm } from "./pages/FeedbackForm";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminInterventions } from "./pages/AdminInterventions";
import { Companies } from "./pages/Companies";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDashboardPage } from "./pages/ProjectDashboardPage";
import { AssignmentsPage } from "./pages/AssignmentsPage";
import { DocumentationPage } from "./pages/DocumentationPage";
import { ControleurDashboard } from "./pages/ControleurDashboard";
import { DevelopmentPage } from "./pages/DevelopmentPage";
import { CompanyReports } from "./pages/CompanyReports";
import { ConfigurationPage } from "./pages/ConfigurationPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { FinalReportPage } from "./pages/FinalReportPage";
import { FinalReportsListPage } from "./pages/FinalReportsListPage";
import { DigitalizationStatsPage } from "./pages/DigitalizationStatsPage";

// Register Service Worker
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('ServiceWorker registered: ', registration.scope);
        })
        .catch((error) => {
          console.log('ServiceWorker registration failed: ', error);
        });
    });
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'consultant') return <Navigate to="/consultant" replace />;
    return <Navigate to="/pme" replace />;
  }

  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'consultant') return <Navigate to="/consultant" replace />;
    if (user.role === 'controleur') return <Navigate to="/controleur" replace />;
    return <Navigate to="/pme" replace />;
  }

  return children;
};

// Home redirect
const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'consultant') return <Navigate to="/consultant" replace />;
  if (user.role === 'controleur') return <Navigate to="/controleur" replace />;
  return <Navigate to="/pme" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Home Redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Profile & Settings (all authenticated users) */}
      <Route path="/profile" element={
        <ProtectedRoute allowedRoles={['admin', 'consultant', 'sme']}>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin', 'consultant', 'sme']}>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Consultant Routes */}
      <Route path="/consultant" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <ConsultantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/consultant/interventions" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <ConsultantInterventions />
        </ProtectedRoute>
      } />
      <Route path="/consultant/interventions/new" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <InterventionForm />
        </ProtectedRoute>
      } />
      <Route path="/consultant/interventions/:id" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <InterventionDetail />
        </ProtectedRoute>
      } />
      <Route path="/consultant/interventions/:id/edit" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <InterventionForm />
        </ProtectedRoute>
      } />
      <Route path="/consultant/companies" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <Companies />
        </ProtectedRoute>
      } />
      <Route path="/consultant/assignment/:assignmentId/final-report" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <FinalReportPage />
        </ProtectedRoute>
      } />

      {/* Final Report View Routes (for admin and SME) */}
      <Route path="/final-report/:assignmentId" element={
        <ProtectedRoute allowedRoles={['admin', 'sme', 'controleur']}>
          <FinalReportPage />
        </ProtectedRoute>
      } />

      {/* PME (SME) Routes */}
      <Route path="/pme" element={
        <ProtectedRoute allowedRoles={['sme']}>
          <SMEDashboard />
        </ProtectedRoute>
      } />
      <Route path="/pme/pending" element={
        <ProtectedRoute allowedRoles={['sme']}>
          <SMEPendingValidations />
        </ProtectedRoute>
      } />
      <Route path="/pme/history" element={
        <ProtectedRoute allowedRoles={['sme']}>
          <SMEHistory />
        </ProtectedRoute>
      } />
      <Route path="/pme/intervention/:id" element={
        <ProtectedRoute allowedRoles={['sme']}>
          <InterventionDetail />
        </ProtectedRoute>
      } />
      <Route path="/pme/feedback/:id" element={
        <ProtectedRoute allowedRoles={['sme']}>
          <FeedbackForm />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Companies />
        </ProtectedRoute>
      } />
      <Route path="/admin/interventions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminInterventions />
        </ProtectedRoute>
      } />
      <Route path="/admin/interventions/:id" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <InterventionDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/projects" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ProjectsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/projects/:projectId" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ProjectDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/projects/:projectId/assignments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AssignmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <CompanyReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/final-reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <FinalReportsListPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/configuration" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ConfigurationPage />
        </ProtectedRoute>
      } />

      {/* Documentation (all authenticated users) */}
      <Route path="/documentation" element={
        <ProtectedRoute allowedRoles={['admin', 'consultant', 'sme', 'controleur']}>
          <DocumentationPage />
        </ProtectedRoute>
      } />

      {/* Development / Feedback (consultants, sme, admin) */}
      <Route path="/development" element={
        <ProtectedRoute allowedRoles={['admin', 'consultant', 'sme']}>
          <DevelopmentPage />
        </ProtectedRoute>
      } />

      {/* Digitalization Stats (admin and controleur) */}
      <Route path="/stats/digitalization" element={
        <ProtectedRoute allowedRoles={['admin', 'controleur']}>
          <DigitalizationStatsPage />
        </ProtectedRoute>
      } />

      {/* Controleur Routes */}
      <Route path="/controleur" element={
        <ProtectedRoute allowedRoles={['controleur']}>
          <ControleurDashboard />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <PWAInstallPrompt />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
